import { Typography } from "@/components/text";
import { useBoolean } from "@/hooks/useBoolean";
import { cn } from "@/libraries/cn";
import { localizeMessageTag } from "@/libraries/helper";
import { extractMentionedUserIdsFromText } from "@/libraries/message";
import { MESSAGE_TAG, type PostCreateMessageBodySchema } from "@/schemas";
import type { GroupMember } from "@/types";
import SendIcon from "@mui/icons-material/Send";
import IconButton from "@mui/material/IconButton";
import type React from "react";
import { Suspense, lazy, useCallback, useMemo, useRef } from "react";
import { isMobile } from "react-device-detect";
import { useFormContext } from "react-hook-form";
import { TextareaAutosizeElement } from "react-hook-form-mui";
import type { TextareaAutosizeElementProps } from "react-hook-form-mui";
import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";

const MemberMentions = lazy(() => import("./MemberMentions"));

interface InputTextProps
	extends React.PropsWithChildren<
		Omit<
			TextareaAutosizeElementProps<PostCreateMessageBodySchema, "text">,
			"control" | "name"
		>
	> {
	type?: "submit" | "button";
	tags?: PostCreateMessageBodySchema["tags"];
	availableMentions?: { id: number; name: string }[];
	members?: GroupMember[];
	onSend?: (
		e: React.MouseEvent<HTMLButtonElement> | KeyboardEvent,
		data?: PostCreateMessageBodySchema,
	) => void;
}

const InputText = ({
	type = "button",
	tags,
	onSend,
	fullWidth = true,
	size = "small",
	placeholder,
	maxRows = 4,
	className,
	members = [],
	hidden,
	children,
	disabled,
	...props
}: InputTextProps) => {
	const { t } = useTranslation("common");
	const defaultPlaceholder = placeholder ?? t("typeMessage");
	const buttonSendRef = useRef<HTMLButtonElement>(null);
	const { control, watch, setValue } =
		useFormContext<PostCreateMessageBodySchema>();
	const messageText = watch("text");
	const isEmptyText = !messageText;
	const currMentions = watch("mentions", []) as number[];
	const {
		value: isOpenMentions,
		setTrue: openMentions,
		setFalse: closeMentions,
	} = useBoolean();

	const isDisabled = useMemo(() => {
		if (typeof disabled === "boolean") {
			return disabled || isEmptyText;
		}
		return isEmptyText;
	}, [disabled, isEmptyText]);

	const availableMentions = useMemo(
		() =>
			members?.map((member) => ({
				id: member.id,
				name: member.fullname,
			})) ?? [],
		[members],
	);

	const handlePaste = useCallback(
		(e: React.ClipboardEvent<HTMLDivElement>) => {
			const pastedText = e.clipboardData.getData("text");
			const mentionUserIds = extractMentionedUserIdsFromText(
				pastedText,
				availableMentions,
			);
			return setValue("mentions", mentionUserIds);
		},
		[availableMentions, setValue],
	);

	const handleOnChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			if (!availableMentions.length) return;
			e.preventDefault();
			if (e.target.value.endsWith("@") && !isOpenMentions) {
				openMentions();
			}
			if (!e.target.value.endsWith("@") && isOpenMentions) {
				closeMentions();
			}
			const mentionUserIds = extractMentionedUserIdsFromText(
				e.target.value,
				availableMentions,
			);
			return setValue("mentions", mentionUserIds);
		},
		[availableMentions, isOpenMentions, openMentions, closeMentions, setValue],
	);

	useHotkeys(
		"enter",
		(e) => {
			e.preventDefault();
			if (type === "button") return onSend?.(e);
			return buttonSendRef.current?.click();
		},
		{
			enabled: !isDisabled && !isMobile,
			enableOnFormTags: ["textarea"],
		},
	);

	return (
		<>
			{isOpenMentions && members.length ? (
				<Suspense>
					<MemberMentions
						members={members}
						onClickmember={(member) => {
							setValue("mentions", [...currMentions, member.id]);
							setValue("text", messageText + member.fullname);
							closeMentions();
						}}
					/>
				</Suspense>
			) : null}
			{children}
			<div
				className={cn(
					"relative flex flex-row justify-center items-center gap-3",
					{
						hidden: hidden,
					},
				)}
			>
				{tags ? (
					<Typography
						fontWeight={500}
						fontSize={12}
						className="px-2 py-1 bg-gray-light rounded-md absolute left-2"
					>
						{localizeMessageTag(tags, t)}
					</Typography>
				) : null}
				<TextareaAutosizeElement
					control={control}
					name="text"
					fullWidth={fullWidth}
					size={size}
					placeholder={defaultPlaceholder}
					aria-label={defaultPlaceholder}
					maxRows={maxRows}
					className={cn(className, "[&>.MuiInputBase-root]:!rounded-xl", {
						"[&_.MuiInputBase-root]:pl-[5.5rem]":
							tags &&
							[MESSAGE_TAG.QUESTION, MESSAGE_TAG.DIAGNOSIS].includes(
								tags as any,
							),
						"[&_.MuiInputBase-root]:pl-16":
							tags &&
							[MESSAGE_TAG.TOPIC, MESSAGE_TAG.PLAN].includes(tags as any),
						"[&_.MuiInputBase-root]:pl-20":
							tags && [MESSAGE_TAG.PUBLISH].includes(tags as any),
					})}
					onPaste={handlePaste}
					onChange={handleOnChange}
					{...props}
				/>
				<IconButton
					ref={buttonSendRef}
					type={type}
					size="large"
					color="primary"
					disabled={isDisabled}
					onClick={onSend}
				>
					<SendIcon />
				</IconButton>
			</div>
		</>
	);
};

export default InputText;
